<script>
/**
 * 공통 페이지네이션 컴포넌트.
 * Vuetify v-pagination을 래핑하며, 페이지당 항목 수 변경 select도 함께 제공한다.
 *
 * Props:
 *   total      - 전체 데이터 건수
 *   page       - 현재 페이지 (1-based)
 *   size       - 페이지당 항목 수 (기본 10)
 *
 * Emits:
 *   update:page  - 페이지 변경 시 새 페이지 번호 emit
 *   update:size  - 페이지당 항목 수 변경 시 새 size emit (페이지는 1로 리셋)
 */
export default {
  name: 'AppPagination',
  props: {
    /** 전체 데이터 건수 */
    total: {
      type: Number,
      required: true,
    },
    /** 현재 페이지 번호 (1-based) */
    page: {
      type: Number,
      default: 1,
    },
    /** 페이지당 항목 수 */
    size: {
      type: Number,
      default: 10,
    },
  },
  emits: ['update:page', 'update:size'],
  computed: {
    /** 전체 페이지 수 계산 (올림 나눗셈) */
    totalPages() {
      if (this.total === 0 || this.size === 0) return 1
      return Math.ceil(this.total / this.size)
    },
    /** 현재 페이지의 시작 번호 (예: 11~20 페이지에서 11) */
    startItem() {
      return (this.page - 1) * this.size + 1
    },
    /** 현재 페이지의 끝 번호 (전체 건수 초과하지 않음) */
    endItem() {
      return Math.min(this.page * this.size, this.total)
    },
  },
  methods: {
    /** 페이지 변경 이벤트 처리 */
    onPageChange(newPage) {
      this.$emit('update:page', newPage)
    },
    /** 페이지당 항목 수 변경 시 페이지를 1로 리셋 */
    onSizeChange(newSize) {
      this.$emit('update:size', Number(newSize))
      this.$emit('update:page', 1)
    },
  },
}
</script>

<template>
  <div v-if="total > 0" class="d-flex align-center justify-space-between flex-wrap ga-2 pt-3">
    <!-- 건수 표시 및 페이지당 항목 수 선택 -->
    <div class="d-flex align-center ga-2">
      <span class="text-caption text-grey ml-3">
        전체 <strong>{{ total }}</strong>건 중
        <strong>{{ startItem }}</strong>–<strong>{{ endItem }}</strong>
      </span>
      <v-select
        :model-value="size"
        :items="[10, 20, 50, 100]"
        density="compact"
        variant="outlined"
        hide-details
        suffix="건"
        @update:model-value="onSizeChange"
      />
    </div>

    <!-- 페이지 버튼 -->
    <v-pagination
      :model-value="page"
      :length="totalPages"
      :total-visible="7"
      density="compact"
      rounded="circle"
      @update:model-value="onPageChange"
    />
  </div>
</template>
